/**
 * ERA (Engagement Risk Assessment) API module.
 *
 * Compliance rules are stored in the DB and editable on the page.
 * The agent compares client-uploaded documents against these rules.
 */
import { authFetch } from './authFetch';

/* ── Types ──────────────────────────────────────────────────────── */

export interface ComplianceRule {
  id: string;
  category: string;
  title: string;
  rule: string;
  severity: string;
  check_type: string;
  guidance: string;
}

export interface ComplianceRuleSetResponse {
  success: boolean;
  rule_set: {
    id: string;
    name: string;
    description: string | null;
    rules: ComplianceRule[];
    source: string;
    updated_at: string | null;
  };
}

export interface EraFinding {
  rule_id: string;
  category: string;
  title: string;
  severity: string;
  finding: 'compliant' | 'non_compliant' | 'needs_review';
  evidence: string;
  recommendation: string;
  risk_score: number;
}

export interface EraResult {
  era_findings: EraFinding[];
  executive_summary: string;
  quality_rating: string;
  total_rules_checked: number;
  compliant_count: number;
  non_compliant_count: number;
  needs_review_count: number;
  recommendations: string[];
}

export interface EraGenerateResponse {
  success: boolean;
  assessment_id: string;
  report_id: string;
  era_result: EraResult;
  files_analyzed?: string[];
  persisted_ruleset_version?: string | null;
  rules_validation?: {
    input_count: number;
    compiled_count: number;
    required_missing: number;
    duplicates_resolved: number;
    malformed_skipped: number;
  } | null;
}

export interface EraReportItem {
  assessment_id: string;
  report_id: string;
  domain: string;
  category: string;
  status: string;
  file_name: string | null;
  proposal_file_names?: string[];
  created_at: string | null;
  expires_at: string | null;
  quality_rating: string;
  findings_count: number;
}

export interface ClientUploadItem {
  id: string;
  filename: string;
  assessment_type: string;
  description: string | null;
  uploaded_by_name: string | null;
  status: string;
  created_at: string | null;
}

export interface EraKnowledgebaseStatus {
  local_folder: string;
  local_folder_resolved?: string | null;
  local_eligible_files?: number;
  local_chunks: number;
  framework_embeddings_enabled: boolean;
  azure_search_configured: boolean;
  kb_azure_sync_enabled: boolean;
  kb_sync_check_seconds: number;
  kb_manifest_path: string;
  kb_manifest_indexed_at: string | null;
  kb_manifest_file_count: number;
  kb_last_sync_result: Record<string, unknown> | null;
  proposal_index_retention_hours: number;
  proposal_cleanup_interval_seconds: number;
  domain: string;
  domain_knowledge_index: boolean;
}

/* ── Compliance Rules API ───────────────────────────────────────── */

export async function getComplianceRules(): Promise<ComplianceRuleSetResponse> {
  const res = await authFetch('/api/era/compliance-rules');
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to load compliance rules');
  }
  return res.json();
}

export async function saveComplianceRules(
  rules: ComplianceRule[],
  name?: string,
): Promise<{ success: boolean; message: string }> {
  const res = await authFetch('/api/era/compliance-rules', {
    method: 'PUT',
    body: JSON.stringify({ rules, name }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to save compliance rules');
  }
  return res.json();
}

export async function uploadComplianceRulesFile(
  file: File,
): Promise<{ success: boolean; message: string; rules?: ComplianceRule[]; raw_text?: string }> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await authFetch('/api/era/compliance-rules/upload', {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to upload compliance rules');
  }
  return res.json();
}

/* ── ERA Generation API ─────────────────────────────────────────── */

const ERA_POLL_INTERVAL_MS = 2000;
const ERA_POLL_TIMEOUT_MS = 900000 ;

interface EraJobSubmitResponse {
  job_id: string;
  status: string;
  message: string;
}

interface EraJobPollResponse {
  job_id: string;
  status: 'processing' | 'completed' | 'failed';
  progress?: string;
  success?: boolean;
  assessment_id?: string;
  report_id?: string;
  era_result?: EraResult;
  files_analyzed?: string[];
  persisted_ruleset_version?: string | null;
  rules_validation?: {
    input_count: number;
    compiled_count: number;
    required_missing: number;
    duplicates_resolved: number;
    malformed_skipped: number;
  } | null;
  error?: string;
}

async function pollEraJob(jobId: string, onProgress?: (msg: string) => void): Promise<EraGenerateResponse> {
  const deadline = Date.now() + ERA_POLL_TIMEOUT_MS;

  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, ERA_POLL_INTERVAL_MS));
    const res = await authFetch(`/api/era/jobs/${jobId}`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `Job polling failed: ${res.status}`);
    }

    const data: EraJobPollResponse = await res.json();
    if (data.progress && onProgress) onProgress(data.progress);
    if (data.status === 'completed') {
      return {
        success: data.success ?? true,
        assessment_id: data.assessment_id!,
        report_id: data.report_id!,
        era_result: data.era_result!,
        files_analyzed: data.files_analyzed,
        persisted_ruleset_version: data.persisted_ruleset_version,
        rules_validation: data.rules_validation,
      };
    }

    if (data.status === 'failed') {
      throw new Error(data.error || 'ERA generation failed');
    }
  }

  throw new Error('ERA generation timed out. The report may still be processing — check reports shortly.');
}

export async function generateEra(
  uploadId: string,
  opts?: {
    chatRules?: string;
    context?: string;
    rulesOverride?: ComplianceRule[];
    reportName?: string;
  },
  onProgress?: (msg: string) => void,
): Promise<EraGenerateResponse> {
  const res = await authFetch('/api/era/generate', {
    method: 'POST',
    body: JSON.stringify({
      upload_id: uploadId,
      chat_rules: opts?.chatRules || '',
      context: opts?.context || '',
      rules_override: opts?.rulesOverride,
      report_name: opts?.reportName || 'ERA Report',
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `ERA generation failed: ${res.status}`);
  }

  const submitData: EraJobSubmitResponse = await res.json();
  return pollEraJob(submitData.job_id, onProgress);
}

export async function generateEraFromFiles(
  proposalFiles: File[],
  opts?: {
    chatRules?: string;
    context?: string;
    rulesOverride?: ComplianceRule[];
    rulesFile?: File;
    reportName: string;
  },
  onProgress?: (msg: string) => void,
): Promise<EraGenerateResponse> {
  const formData = new FormData();
  proposalFiles.forEach((file) => formData.append('proposal_files', file));
  if (opts?.chatRules) formData.append('chat_rules', opts.chatRules);
  if (opts?.context) formData.append('context', opts.context);
  if (opts?.rulesOverride) formData.append('rules_override', JSON.stringify(opts.rulesOverride));
  if (opts?.rulesFile) formData.append('rules_file', opts.rulesFile);
  if (opts?.reportName) formData.append('report_name', opts.reportName);

  const res = await authFetch('/api/era/generate-from-files', {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `ERA generation failed: ${res.status}`);
  }

  const submitData: EraJobSubmitResponse = await res.json();
  return pollEraJob(submitData.job_id, onProgress);
}

export async function listEraReports(): Promise<EraReportItem[]> {
  const res = await authFetch('/api/era/reports');
  if (!res.ok) return [];
  const data = await res.json();
  return data.reports || [];
}

export async function downloadEraReport(reportId: string): Promise<Blob> {
  const res = await authFetch(`/api/era/reports/${reportId}/download`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to download ERA report');
  }
  return res.blob();
}

export async function listClientUploadsForEra(): Promise<ClientUploadItem[]> {
  const res = await authFetch('/api/era/client-uploads');
  if (!res.ok) return [];
  const data = await res.json();
  return data.uploads || [];
}

export async function getEraKnowledgebaseStatus(): Promise<EraKnowledgebaseStatus> {
  const res = await authFetch('/api/era/knowledgebase/status');
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to load ERA knowledgebase status');
  }
  return res.json();
}

export async function syncEraKnowledgebase(
  force = false,
): Promise<{ success: boolean; force: boolean; result: Record<string, unknown> | null }> {
  const res = await authFetch(`/api/era/knowledgebase/sync?force=${force ? '1' : '0'}`, {
    method: 'POST',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to sync ERA knowledgebase');
  }
  return res.json();
}

export async function clearEraProposalIndexes(): Promise<{
  success: boolean;
  result: Record<string, unknown> | null;
}> {
  const res = await authFetch('/api/era/knowledgebase/proposal-index/clear', {
    method: 'POST',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to clear ERA proposal indexes');
  }
  return res.json();
}
